import React, { useState, useEffect, useMemo } from 'react';
import { CreditCard, Search, Loader2, CheckCircle2, XCircle, Clock, Download, FileDown } from 'lucide-react';
import * as XLSX from 'xlsx';
import { adminApi } from '../services/api.jsx';
import PiedTableau from '../components/PiedTableau';
import { useDroits } from '../context/Droits';
import PaiementsIntervenants from '../components/PaiementsIntervenants';
import RetraitsATraiter from '../components/RetraitsATraiter';
import {
  annoncerErreur, annoncerSucces, EtatErreur, EtatSansDonnees, Squelette, SqueletteTableau, telecharger,
} from '../etats';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

async function downloadInvoice(diagnosticId, orgName, setDownloading) {
  setDownloading(diagnosticId);
  try {
    // Avec le jeton : le reçu n'est plus servi à qui connaît l'identifiant.
    await telecharger(`${API_BASE}/payments/invoice/${diagnosticId}/`,
      `recu_${(orgName || 'paiement').replace(/\s+/g, '_')}.pdf`);
    annoncerSucces('Reçu téléchargé.');
  } catch (err) {
    annoncerErreur(`Impossible de télécharger le reçu : ${err.message}`);
  } finally {
    setDownloading(null);
  }
}

const STATUS_CONFIG = {
  completed: { label: 'Complété', icon: CheckCircle2, color: 'text-[var(--accent-texte)]', bg: 'bg-[var(--accent)]/10 border-[var(--border)]' },
  pending:   { label: 'En attente', icon: Clock, color: 'text-[var(--attention)]', bg: 'bg-[var(--attention)]/10 border-[var(--border)]' },
  failed:    { label: 'Échoué', icon: XCircle, color: 'text-[var(--alerte)]', bg: 'bg-[var(--alerte)]/10 border-[var(--border)]' },
};

export default function Payments({ user }) {
  // Les boutons d'export ne s'affichent que si le droit est ouvert.
  const { peut } = useDroits();
  /* Deux caisses distinctes, et les confondre aurait été une erreur de
     lecture : ce qui ENTRE (les diagnostics payés par les organisations)
     et ce qui SORT (les relevés de prestation des intervenants). Le même
     écran, parce que c'est la même personne qui les tient ; deux onglets,
     parce que ce ne sont pas les mêmes montants. */
  const [caisse, setCaisse] = useState('entrees');
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getPayments();
      setPayments(data || []);
      setErreur(null);
    } catch (error) {
      console.error('Erreur chargement paiements:', error);
      setErreur(error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return payments.filter(p => {
      const matchesSearch =
        (p.customer_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.customer_email || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.transaction_id || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.organisation_nom || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [payments, search, statusFilter]);

  const stats = useMemo(() => ({
    total: payments.length,
    completed: payments.filter(p => p.status === 'completed').length,
    pending: payments.filter(p => p.status === 'pending').length,
    failed: payments.filter(p => p.status === 'failed').length,
    revenue: payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + p.amount, 0),
  }), [payments]);

  const handleExport = () => {
    const rows = filtered.map(p => ({
      'ID Transaction': p.transaction_id,
      'Organisation': p.organisation_nom,
      'Client': p.customer_name,
      'Email': p.customer_email,
      'Montant': p.amount,
      'Devise': p.currency,
      'Statut': STATUS_CONFIG[p.status]?.label || p.status,
      'Date': new Date(p.created_at).toLocaleDateString('fr-FR'),
      'Heure': new Date(p.created_at).toLocaleTimeString('fr-FR'),
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Transactions');
    XLSX.writeFile(wb, `PAIEMENTS_ASSOPRO_${new Date().toISOString().split('T')[0]}.xlsx`);
    annoncerSucces(`${rows.length} transaction${rows.length > 1 ? 's' : ''} exportée${rows.length > 1 ? 's' : ''}.`);
  };

  return (
    <div className="space-y-8 animate-in">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6">
        <div>
          <h1 className="titre-page">Paiements</h1>
          {/* Le sous-titre suit l'onglet : « paiements Paystack » décrirait
              faussement la caisse des sorties, qui ne passe par aucune
              passerelle — un virement ou des espèces contre reçu. */}
          <p className="text-[var(--text-muted)] font-medium mt-2">
            {caisse === 'sorties'
              ? "Décaissement des relevés de prestation des intervenants."
              : caisse === 'retraits'
                ? "Demandes de retrait des intervenants, sur le solde de leurs relevés validés."
                : "Suivi des paiements Paystack pour les rapports de diagnostic."}
          </p>
        </div>

        {caisse === 'entrees' && peut('paiements', 'exporter') && (
          <button
            onClick={handleExport}
            disabled={loading || filtered.length === 0}
            className="act act-contour"
          >
            <Download className="w-4 h-4" />
            Exporter (.xlsx)
          </button>
        )}
      </div>

      <div className="flex gap-1 flex-wrap border-b border-[var(--border)] pb-px">
        {[
          { cle: 'entrees', libelle: 'Paiements reçus' },
          { cle: 'sorties', libelle: 'Relevés à décaisser' },
          // Le relevé dit ce qui est dû, le retrait ce qu'on en demande.
          // Deux onglets, parce que ce ne sont pas les mêmes montants ni
          // les mêmes gestes.
          { cle: 'retraits', libelle: 'Retraits demandés' },
        ].map((o) => {
          const actif = caisse === o.cle;
          return (
            <button
              key={o.cle}
              type="button"
              onClick={() => setCaisse(o.cle)}
              style={{ minHeight: 44,
                       color: actif ? 'var(--text-bold)' : 'var(--text-muted)',
                       boxShadow: actif ? 'inset 0 -2px 0 var(--accent)' : 'none' }}
              className="act act-tertiaire"
            >
              {o.libelle}
            </button>
          );
        })}
      </div>

      {caisse === 'sorties' && <PaiementsIntervenants user={user} />}
      {caisse === 'retraits' && <RetraitsATraiter user={user} />}

      {caisse === 'entrees' && (<>
      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total', value: stats.total, accent: false },
          { label: 'Complétés', value: stats.completed, accent: true },
          { label: 'En attente', value: stats.pending, accent: false },
          { label: 'Échoués', value: stats.failed, accent: false },
          { label: 'Revenus', value: `${stats.revenue.toLocaleString()} XOF`, accent: true },
        ].map(({ label, value, accent }) => (
          <div key={label} className={`rounded-pastille border p-4 flex flex-col items-center justify-center ${accent ? 'bg-[var(--accent)]/5 border-[var(--accent)]/20' : 'bg-[var(--bg-card)] border-[var(--border)]'}`}>
            <span className="text-[9px] font-semibold uppercase text-[var(--text-muted)]">{label}</span>
            {/* Pas de « 0 » pendant le chargement : il serait lu comme un chiffre. */}
            {loading || erreur
              ? <Squelette className="h-5 w-12 mt-1" />
              : <span className={`text-lg font-semibold ${accent ? 'text-[var(--accent-texte)]' : 'text-[var(--text-bold)]'}`}>{value}</span>}
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2 relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-muted)]" />
          <input
            type="text"
            placeholder="Rechercher par nom, email, transaction, organisation..."
            className="champ champ-loupe font-bold"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="filtre"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all" className="bg-[var(--bg-card)] text-[var(--text-bold)]">Tous les statuts</option>
          <option value="completed" className="bg-[var(--bg-card)] text-[var(--text-bold)]">Complétés</option>
          <option value="pending" className="bg-[var(--bg-card)] text-[var(--text-bold)]">En attente</option>
          <option value="failed" className="bg-[var(--bg-card)] text-[var(--text-bold)]">Échoués</option>
        </select>
      </div>

      {/* Table */}
      {loading ? (
        <SqueletteTableau colonnes={7} />
      ) : erreur ? (
        <div className="carte-tableau"><EtatErreur erreur={erreur} onReessayer={loadData} /></div>
      ) : (
      <div className="carte-tableau">
        <div className="tableau-hote">
          <table className="tableau-cartes w-full text-left">
            <thead className="">
              <tr>
                <th className="">Organisation</th>
                <th className="">Client</th>
                <th className="">Montant</th>
                <th className="">Statut</th>
                <th className="">Transaction</th>
                <th className="">Date</th>
                <th className="">Reçu</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border)]">
              {filtered.map(p => {
                const cfg = STATUS_CONFIG[p.status] || STATUS_CONFIG.pending;
                const StatusIcon = cfg.icon;
                return (
                  <tr key={p.id} className="hover:bg-[var(--bg-main)] transition-all group">
                    <td data-libelle="Organisation" className="px-6 py-5">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-pastille bg-[var(--bg-card)] flex items-center justify-center text-[var(--accent-texte)] font-semibold border border-[var(--border)] text-sm group-hover:bg-[var(--accent)] group-hover:text-[#0A1128] transition-all">
                          {(p.organisation_nom || '?')[0]}
                        </div>
                        <span className="text-xs font-bold text-[var(--text-bold)] uppercase">{p.organisation_nom || '—'}</span>
                      </div>
                    </td>
                    <td data-libelle="Client" className="px-6 py-5">
                      <div className="flex flex-col">
                        <span className="text-xs font-bold text-[var(--text-bold)]">{p.customer_name || '—'}</span>
                        <span className="text-[10px] text-[var(--text-muted)]">{p.customer_email || ''}</span>
                      </div>
                    </td>
                    <td data-libelle="Montant" className="px-6 py-5 text-sm font-semibold text-[var(--text-bold)]">
                      {p.amount.toLocaleString()} <span className="text-[10px] font-bold text-[var(--text-muted)]">{p.currency}</span>
                    </td>
                    <td data-libelle="Statut" className="px-6 py-5">
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-controle border text-[10px] font-semibold uppercase ${cfg.bg} ${cfg.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {cfg.label}
                      </span>
                    </td>
                    <td data-libelle="Transaction" className="px-6 py-5 text-[10px] font-mono text-[var(--text-muted)]">
                      {p.transaction_id.substring(0, 8)}...
                    </td>
                    <td data-libelle="Date" className="px-6 py-5 text-xs text-[var(--text-muted)]">
                      {new Date(p.created_at).toLocaleDateString('fr-FR')}<br />
                      <span className="text-[10px]">{new Date(p.created_at).toLocaleTimeString('fr-FR')}</span>
                    </td>
                    <td data-libelle="Reçu" className="px-6 py-5">
                      {p.status === 'completed' && p.diagnostic_id ? (
                        <button
                          onClick={() => downloadInvoice(p.diagnostic_id, p.organisation_nom, setDownloadingId)}
                          disabled={downloadingId === p.diagnostic_id}
                          aria-busy={downloadingId === p.diagnostic_id || undefined}
                          title="Télécharger le reçu de paiement PDF"
                          className="act"
                        >
                          {downloadingId === p.diagnostic_id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <FileDown className="w-3 h-3" />}
                          Reçu
                        </button>
                      ) : (
                        <span className="text-[10px] text-[var(--text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 ? (
          payments.length === 0 ? (
            <EtatSansDonnees cas="vide" icone={CreditCard} titre="Aucun paiement reçu"
              aide="Les paiements des rapports de diagnostic apparaîtront ici." />
          ) : search.trim() ? (
            <EtatSansDonnees cas="sansResultat" titre={`Aucune transaction pour « ${search.trim()} »`}
              action={<button type="button" className="act act-sm act-contour" onClick={() => setSearch('')}>Effacer la recherche</button>} />
          ) : (
            <EtatSansDonnees cas="sansFiltre" titre="Aucune transaction avec ce statut"
              action={<button type="button" className="act act-sm act-contour" onClick={() => setStatusFilter('all')}>Réinitialiser</button>} />
          )
        ) : (
          <PiedTableau affiches={filtered.length} total={payments.length}
                       nom="transactions" />
        )}
      </div>
      )}
      </>)}
    </div>
  );
}
